import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Search, Users, Utensils, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useExploreCity } from "@/contexts/ExploreCityContext";
import { formatDistance } from "@/lib/distance";
import { FButton } from "@/components/forkd/FButton";
import { RestaurantDetailSheet } from "@/components/forkd/RestaurantDetailSheet";
import { RestaurantPhoto } from "@/components/forkd/RestaurantPhoto";
import {
  searchGooglePlaces,
  upsertRestaurantFromPlace,
  backfillRestaurantCuisineLinks,
  type GooglePlace,
} from "@/lib/google";



type City = { id: string; name: string; lat: number; lng: number };
type Cuisine = {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  parent_id: string | null;
};

type RestaurantHit = {
  id: string;
  name: string;
  photo_url: string | null;
  visit_count: number;
  neighbourhood: { name: string } | null;
  city_id: string;
  cuisines: { name: string; emoji: string | null }[];
};

type NearbyRow = {
  id: string;
  name: string;
  photo_url: string | null;
  visit_count: number;
  distance_metres: number;
  cuisine_names: string[] | null;
  cuisine_emojis: string[] | null;
  neighbourhood_name: string | null;
  city_id: string;
};

export default function Explore() {
  const { user } = useAuth();
  const nav = useNavigate();

  // One-shot backfill for restaurants missing cuisine links.
  useEffect(() => {
    backfillRestaurantCuisineLinks().catch(() => {});
  }, []);



  const [cities, setCities] = useState<City[]>([]);
  const { cityId, setCityId } = useExploreCity();
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [parents, setParents] = useState<Cuisine[]>([]);
  const [cuisineCounts, setCuisineCounts] = useState<Record<string, number>>({});

  const [query, setQuery] = useState("");
  const [restaurantHits, setRestaurantHits] = useState<RestaurantHit[]>([]);
  const [cuisineHits, setCuisineHits] = useState<
    (Cuisine & { visit_count: number })[]
  >([]);
  const [googleHits, setGoogleHits] = useState<GooglePlace[]>([]);
  const [searching, setSearching] = useState(false);
  const [openingPlace, setOpeningPlace] = useState<string | null>(null);


  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [geoState, setGeoState] = useState<"idle" | "denied" | "granted" | "error">("idle");
  const [nearby, setNearby] = useState<NearbyRow[]>([]);

  const [openRestaurant, setOpenRestaurant] = useState<string | null>(null);

  const city = useMemo(() => cities.find((c) => c.id === cityId) ?? null, [cities, cityId]);

  // Load cities + default
  useEffect(() => {
    (async () => {
      const [{ data: cityRows }, profRes] = await Promise.all([
        supabase
          .from("cities")
          .select("id, name, lat, lng")
          .eq("is_active", true)
          .order("name"),
        user
          ? supabase
              .from("profiles")
              .select("home_city_id")
              .eq("id", user.id)
              .maybeSingle()
          : Promise.resolve({ data: null } as any),
      ]);
      const list = (cityRows ?? []) as City[];
      setCities(list);
      if (cityId) return; // keep user's selected explore city
      const home = (profRes as any)?.data?.home_city_id as string | undefined;
      const london = list.find((c) => c.name === "London");
      setCityId(home ?? london?.id ?? list[0]?.id ?? null);
    })();
  }, [user, cityId, setCityId]);

  // Load parent cuisines once
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("cuisine_types")
        .select("id, name, slug, emoji, parent_id")
        .is("parent_id", null)
        .eq("is_active", true)
        .order("display_order");
      setParents((data ?? []) as Cuisine[]);
    })();
  }, []);

  // Load per-parent counts for the current city
  useEffect(() => {
    if (!cityId || parents.length === 0) return;
    (async () => {
      // get every cuisine type with parent_id for mapping children → parent
      const { data: allTypes } = await supabase
        .from("cuisine_types")
        .select("id, parent_id");
      const childToParent = new Map<string, string>();
      for (const c of (allTypes ?? []) as any[]) {
        childToParent.set(c.id, c.parent_id ?? c.id);
      }
      // Get all restaurant_cuisine_types rows for restaurants in this city
      const { data: rels } = await supabase
        .from("restaurant_cuisine_types")
        .select("cuisine_type_id, restaurants!inner(id, city_id)")
        .eq("restaurants.city_id", cityId);
      const seen: Record<string, Set<string>> = {};
      for (const row of (rels ?? []) as any[]) {
        const parent = childToParent.get(row.cuisine_type_id) ?? row.cuisine_type_id;
        if (!seen[parent]) seen[parent] = new Set();
        seen[parent].add(row.restaurants.id);
      }
      const counts: Record<string, number> = {};
      for (const p of parents) counts[p.id] = seen[p.id]?.size ?? 0;
      setCuisineCounts(counts);
    })();
  }, [cityId, parents]);

  // Search
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setRestaurantHits([]);
      setCuisineHits([]);
      setGoogleHits([]);
      return;
    }
    setSearching(true);
    const h = setTimeout(async () => {
      const [restRes, ctRes] = await Promise.all([
        supabase
          .from("restaurants")
          .select(
            "id, name, photo_url, visit_count, city_id, google_place_id, neighbourhood:neighbourhoods(name), restaurant_cuisine_types(cuisine_types(name, emoji))",
          )
          .ilike("name", `%${q}%`)
          .eq("is_active", true)
          .limit(8),
        supabase
          .from("cuisine_types")
          .select("id, name, slug, emoji, parent_id")
          .ilike("name", `%${q}%`)
          .eq("is_active", true)
          .limit(6),
      ]);

      const restRows = (restRes.data ?? []) as any[];
      setRestaurantHits(
        restRows.map((r) => ({
          id: r.id,
          name: r.name,
          photo_url: r.photo_url,
          visit_count: r.visit_count,
          city_id: r.city_id,
          neighbourhood: r.neighbourhood,
          cuisines: (r.restaurant_cuisine_types ?? [])
            .map((x: any) => x.cuisine_types)
            .filter(Boolean),
        })),
      );

      // For each cuisine hit, count visits across restaurants of that type
      const ctList = (ctRes.data ?? []) as Cuisine[];
      const counts: Record<string, number> = {};
      await Promise.all(
        ctList.map(async (c) => {
          const { data: rels } = await supabase
            .from("restaurant_cuisine_types")
            .select("restaurants!inner(visit_count)")
            .eq("cuisine_type_id", c.id);
          counts[c.id] = (rels ?? []).reduce(
            (a: number, r: any) => a + (r.restaurants?.visit_count ?? 0),
            0,
          );
        }),
      );
      setCuisineHits(ctList.map((c) => ({ ...c, visit_count: counts[c.id] ?? 0 })));

      // Fall back to Google Places when Supabase has fewer than 3 restaurants.
      if (restRows.length < 3) {
        const bias = city ?? cities.find((c) => c.id === cityId) ?? null;
        const places = await searchGooglePlaces(
          q,
          bias?.lat,
          bias?.lng,
        );
        const existingPlaceIds = new Set(
          restRows.map((r) => r.google_place_id).filter(Boolean),
        );
        setGoogleHits(places.filter((p) => !existingPlaceIds.has(p.place_id)));
      } else {
        setGoogleHits([]);
      }

      setSearching(false);
    }, 400);
    return () => clearTimeout(h);
  }, [query, cityId, cities, city]);

  const handleOpenGooglePlace = useCallback(async (place: GooglePlace) => {
    setOpeningPlace(place.place_id);
    const saved = await upsertRestaurantFromPlace(place);
    setOpeningPlace(null);
    if (saved) setOpenRestaurant(saved.id);
  }, []);


  // Geolocation for nearby — must be triggered by a user gesture (iOS Safari).
  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeoState("error");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoState("granted");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGeoState("denied");
        } else {
          setGeoState("error");
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  }, []);


  // Load nearby recents when we have coords
  useEffect(() => {
    if (!coords) return;
    (async () => {
      const { data } = await supabase.rpc("get_nearby_restaurants", {
        user_lat: coords.lat,
        user_lng: coords.lng,
        radius_metres: 2000,
        result_limit: 30,
      });
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const rows = ((data ?? []) as NearbyRow[]).filter((r) => r.visit_count > 0);
      if (rows.length === 0) {
        setNearby([]);
        return;
      }
      const ids = rows.map((r) => r.id);
      const { data: recentVisits } = await supabase
        .from("visits")
        .select("restaurant_id, confirmed_at")
        .in("restaurant_id", ids)
        .gte("confirmed_at", since)
        .order("confirmed_at", { ascending: false });
      const latest = new Map<string, string>();
      for (const v of (recentVisits ?? []) as any[]) {
        if (!latest.has(v.restaurant_id)) latest.set(v.restaurant_id, v.confirmed_at);
      }
      const filtered = rows
        .filter((r) => latest.has(r.id))
        .sort(
          (a, b) =>
            new Date(latest.get(b.id)!).getTime() -
            new Date(latest.get(a.id)!).getTime(),
        );
      setNearby(filtered);
    })();
  }, [coords]);

  return (
    <div className="pt-2 pb-8">
      <header className="px-4 h-14 flex items-center">
        <h1 className="text-[22px] font-bold">Explore</h1>
      </header>

      {/* Search */}
      <div className="px-4">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--colour-text-secondary)]"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search restaurants, cuisines..."
            className="w-full h-11 pl-9 pr-3 rounded-[var(--radius-md)] text-[15px] outline-none"
            style={{
              backgroundColor: "var(--colour-surface-raised)",
              color: "var(--colour-text-primary)",
            }}
          />
        </div>
      </div>

      {/* City bar */}
      <button
        onClick={() => setShowCityPicker(true)}
        className="mt-3 mx-4 inline-flex items-center gap-1.5 text-[13px] text-[var(--colour-text-secondary)] h-8 px-2 -ml-2"
      >
        <MapPin size={14} />
        <span className="font-medium text-[var(--colour-text-primary)]">
          {city?.name ?? "Loading..."}
        </span>
      </button>

      {/* Search results */}
      {query.trim() && (
        <section className="px-4 mt-2">
          {searching ? (
            <p className="text-[13px] text-[var(--colour-text-secondary)] py-4">
              Searching…
            </p>
          ) : restaurantHits.length === 0 && cuisineHits.length === 0 && googleHits.length === 0 ? (
            <p className="text-[13px] text-[var(--colour-text-secondary)] py-4">
              No matches.
            </p>

          ) : (
            <div className="space-y-4">
              {cuisineHits.length > 0 && (
                <div>
                  <h3 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--colour-text-secondary)] mb-2">
                    Cuisines
                  </h3>
                  <ul className="space-y-2">
                    {cuisineHits.map((c) => (
                      <li key={c.id}>
                        <Link
                          to={`/cuisine/${c.slug}`}
                          className="flex items-center gap-3 h-14 px-3 rounded-[var(--radius-md)] border"
                          style={{
                            borderColor: "var(--colour-border)",
                            backgroundColor: "var(--colour-surface)",
                          }}
                        >
                          <span className="text-[24px]">{c.emoji ?? "🍽️"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-semibold">{c.name}</div>
                            <div className="text-[12px] text-[var(--colour-text-secondary)]">
                              {c.visit_count} visit{c.visit_count === 1 ? "" : "s"}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {restaurantHits.length > 0 && (
                <div>
                  <h3 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--colour-text-secondary)] mb-2">
                    Restaurants
                  </h3>
                  <ul className="space-y-2">
                    {restaurantHits.map((r) => (
                      <li key={r.id}>
                        <button
                          onClick={() => setOpenRestaurant(r.id)}
                          className="w-full text-left flex items-center gap-3 h-16 px-3 rounded-[var(--radius-md)] border"
                          style={{
                            borderColor: "var(--colour-border)",
                            backgroundColor: "var(--colour-surface)",
                          }}
                        >
                          <span className="text-[22px] w-10 text-center">
                            {r.cuisines[0]?.emoji ?? "🍽️"}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-semibold truncate">
                              {r.name}
                            </div>
                            <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                              {r.cuisines[0]?.name ?? "Restaurant"}
                              {r.neighbourhood?.name ? ` · ${r.neighbourhood.name}` : ""}
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {googleHits.length > 0 && (
                <div>
                  <h3 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--colour-text-secondary)] mb-2">
                    From Google
                  </h3>
                  <ul className="space-y-2">
                    {googleHits.map((p) => (
                      <li key={p.place_id}>
                        <button
                          onClick={() => handleOpenGooglePlace(p)}
                          disabled={openingPlace === p.place_id}
                          className="w-full text-left flex items-center gap-3 h-16 px-3 rounded-[var(--radius-md)] border disabled:opacity-60"
                          style={{
                            borderColor: "var(--colour-border)",
                            backgroundColor: "var(--colour-surface)",
                          }}
                        >
                          <span className="text-[22px] w-10 text-center">📍</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-semibold truncate">
                              {p.name}
                            </div>
                            <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                              {p.address ?? "Restaurant"}
                            </div>
                          </div>
                          {openingPlace === p.place_id && (
                            <span className="text-[12px] text-[var(--colour-text-secondary)]">
                              Adding…
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      )}


      {/* Cuisine grid */}
      {!query.trim() && (
        <section className="px-4 mt-4">
          <h2 className="text-[16px] font-semibold mb-3">Explore by cuisine</h2>
          <div className="grid grid-cols-2 gap-2.5">
            {parents.map((p) => (
              <Link
                key={p.id}
                to={`/cuisine/${p.slug}`}
                className="flex flex-col items-center justify-center text-center py-5 rounded-[var(--radius-md)]"
                style={{ backgroundColor: "var(--colour-surface-raised)" }}
              >
                <span className="text-[36px] leading-none">{p.emoji ?? "🍽️"}</span>
                <span className="mt-2 text-[14px] font-semibold">{p.name}</span>
                <span className="mt-0.5 text-[11px] text-[var(--colour-text-secondary)]">
                  {cuisineCounts[p.id] ?? 0} restaurant
                  {cuisineCounts[p.id] === 1 ? "" : "s"}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recently logged nearby */}
      {!query.trim() && (
        <section className="mt-7">
          <h2 className="px-4 text-[16px] font-semibold mb-3">
            Recently logged near you
          </h2>
          {geoState === "idle" ? (
            <div className="mx-4 p-5 rounded-[var(--radius-md)] border text-center"
              style={{
                borderColor: "var(--colour-border)",
                backgroundColor: "var(--colour-surface)",
              }}>
              <MapPin
                size={28}
                className="mx-auto mb-2 text-[var(--colour-text-secondary)]"
              />
              <p className="text-[14px] text-[var(--colour-text-secondary)] mb-3">
                Allow location to see what's popular near you
              </p>
              <FButton onClick={requestLocation}>Enable location</FButton>
            </div>
          ) : geoState === "denied" ? (
            <div className="mx-4 p-5 rounded-[var(--radius-md)] border text-center"
              style={{
                borderColor: "var(--colour-border)",
                backgroundColor: "var(--colour-surface)",
              }}>
              <MapPin
                size={28}
                className="mx-auto mb-2 text-[var(--colour-text-secondary)]"
              />
              <p className="text-[14px] text-[var(--colour-text-secondary)]">
                Location access was denied. You can enable it in your browser settings.
              </p>
            </div>
          ) : geoState === "error" ? (
            <div className="mx-4 p-5 rounded-[var(--radius-md)] border text-center"
              style={{
                borderColor: "var(--colour-border)",
                backgroundColor: "var(--colour-surface)",
              }}>
              <MapPin
                size={28}
                className="mx-auto mb-2 text-[var(--colour-text-secondary)]"
              />
              <p className="text-[14px] text-[var(--colour-text-secondary)] mb-3">
                Could not get your location. Please try again.
              </p>
              <FButton onClick={requestLocation}>Retry</FButton>
            </div>

          ) : nearby.length === 0 ? (
            <div className="mx-4 p-5 rounded-[var(--radius-md)] border text-center"
              style={{
                borderColor: "var(--colour-border)",
                backgroundColor: "var(--colour-surface)",
              }}>
              <div className="text-[44px] mb-1" aria-hidden>📍</div>
              <h3 className="text-[15px] font-bold">Nothing logged nearby recently</h3>
              <p className="text-[13px] text-[var(--colour-text-secondary)] mt-1">
                Be the first to log a restaurant in your area.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto px-4">
              <ul className="flex gap-3 min-w-min">
                {nearby.map((r) => (
                  <li key={r.id} className="shrink-0 w-[180px]">
                    <button
                      onClick={() => setOpenRestaurant(r.id)}
                      className="text-left w-full rounded-[var(--radius-md)] overflow-hidden border"
                      style={{
                        borderColor: "var(--colour-border)",
                        backgroundColor: "var(--colour-surface)",
                      }}
                    >
                      <div
                        className="aspect-[4/3] flex items-center justify-center text-[42px]"
                        style={{ backgroundColor: "var(--colour-surface-raised)" }}
                      >
                        <RestaurantPhoto
                          restaurantId={r.id}
                          photoUrl={r.photo_url}
                          emoji={r.cuisine_emojis?.[0] ?? "🍽️"}
                          alt={r.name}
                        />
                      </div>
                      <div className="p-2.5">
                        <div className="text-[13px] font-semibold truncate">
                          {r.name}
                        </div>
                        <div className="text-[11px] text-[var(--colour-text-secondary)] truncate">
                          {r.cuisine_emojis?.[0] ?? ""} {r.cuisine_names?.[0] ?? "Restaurant"}
                        </div>
                        <div className="mt-1 text-[11px] text-[var(--colour-text-secondary)] flex items-center gap-2">
                          <span className="inline-flex items-center gap-0.5">
                            <Users size={11} />
                            {r.visit_count}
                          </span>
                          <span>{formatDistance(r.distance_metres)}</span>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <RestaurantDetailSheet
        restaurantId={openRestaurant}
        open={openRestaurant !== null}
        onClose={() => setOpenRestaurant(null)}
      />

      {/* City picker sheet */}
      {showCityPicker && (
        <div className="fixed inset-0 z-50 flex items-end">
          <button
            aria-label="Close"
            onClick={() => setShowCityPicker(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div
            className="relative w-full max-h-[70vh] flex flex-col"
            style={{
              backgroundColor: "var(--colour-surface)",
              borderTopLeftRadius: "var(--radius-lg)",
              borderTopRightRadius: "var(--radius-lg)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <div className="pt-2.5 pb-1 flex justify-center">
              <div
                className="w-10 h-1 rounded-full"
                style={{ backgroundColor: "var(--colour-border)" }}
              />
            </div>
            <div className="px-4 h-12 flex items-center justify-between">
              <h3 className="text-[16px] font-semibold">Choose city</h3>
              <button
                onClick={() => setShowCityPicker(false)}
                className="w-11 h-11 -mr-3 flex items-center justify-center"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto pb-4">
              {cities.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => {
                      setCityId(c.id);
                      setShowCityPicker(false);
                    }}
                    className="w-full text-left px-5 h-12 flex items-center justify-between"
                  >
                    <span className="text-[15px]">{c.name}</span>
                    {c.id === cityId && (
                      <span className="text-[var(--colour-accent)] text-[13px] font-semibold">
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
